import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { ArrowLeftRight, Play, Pause, RotateCcw } from "lucide-react";

interface TwoBodyState {
	pos1: number;
	pos2: number;
	vel1: number;
	vel2: number;
}

export function Lesson3ActionReaction() {
	const [pushForce, setPushForce] = useState(40);
	const [mass1, setMass1] = useState(5);
	const [mass2, setMass2] = useState(10);
	const [bodyState, setBodyState] = useState<TwoBodyState>({
		pos1: -1,
		pos2: 1,
		vel1: 0,
		vel2: 0,
	});
	const [isRunning, setIsRunning] = useState(false);
	const [hasCollided, setHasCollided] = useState(false);
	const [phase, setPhase] = useState<"approach" | "contact" | "separate">(
		"approach",
	);

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animRef = useRef<number | null>(null);
	const lastTimeRef = useRef<number>(0);

	const reset = () => {
		setIsRunning(false);
		setHasCollided(false);
		setPhase("approach");
		setBodyState({ pos1: -1, pos2: 1, vel1: 0, vel2: 0 });
		lastTimeRef.current = 0;
	};

	const simulate = useCallback(
		(timestamp: number) => {
			if (lastTimeRef.current === 0) lastTimeRef.current = timestamp;
			const dt = Math.min(
				(timestamp - lastTimeRef.current) / 1000,
				0.05,
			);
			lastTimeRef.current = timestamp;

			setBodyState((prev) => {
				const gap = prev.pos2 - prev.pos1;

				if (phase === "approach") {
					// Move block 1 toward block 2
					const approachVel = 3;
					const newPos1 = prev.pos1 + approachVel * dt;

					if (newPos1 >= prev.pos2 - 0.3) {
						setPhase("contact");
						setHasCollided(true);
						// Elastic collision
						const v1 =
							((mass1 - mass2) / (mass1 + mass2)) * approachVel;
						const v2 =
							((2 * mass1) / (mass1 + mass2)) * approachVel;
						return {
							...prev,
							pos1: prev.pos2 - 0.3,
							vel1: v1,
							vel2: v2,
						};
					}

					return { ...prev, pos1: newPos1, vel1: approachVel, vel2: 0 };
				}

				if (phase === "contact") {
					setPhase("separate");
					return prev;
				}

				// Separate phase
				return {
					pos1: prev.pos1 + prev.vel1 * dt,
					pos2: prev.pos2 + prev.vel2 * dt,
					vel1: prev.vel1 * 0.999,
					vel2: prev.vel2 * 0.999,
				};
			});

			animRef.current = requestAnimationFrame(simulate);
		},
		[mass1, mass2, phase],
	);

	useEffect(() => {
		if (isRunning) {
			lastTimeRef.current = 0;
			animRef.current = requestAnimationFrame(simulate);
		} else if (animRef.current) {
			cancelAnimationFrame(animRef.current);
		}
		return () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
		};
	}, [isRunning, simulate]);

	// Draw the two-body canvas
	const draw = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const dpr = window.devicePixelRatio || 1;
		const w = 700;
		const h = 280;
		canvas.width = w * dpr;
		canvas.height = h * dpr;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		ctx.save();
		ctx.scale(dpr, dpr);

		// Background
		ctx.fillStyle = "#0f172a";
		ctx.fillRect(0, 0, w, h);

		// Grid
		ctx.strokeStyle = "#1e293b";
		ctx.lineWidth = 1;
		for (let x = 0; x < w; x += 40) {
			ctx.beginPath();
			ctx.moveTo(x, 0);
			ctx.lineTo(x, h);
			ctx.stroke();
		}
		for (let y = 0; y < h; y += 40) {
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(w, y);
			ctx.stroke();
		}

		const groundY = h - 60;

		// Ground
		ctx.fillStyle = "#9ca3af";
		ctx.fillRect(0, groundY, w, 8);
		ctx.fillStyle = "rgba(100,100,100,0.3)";
		ctx.fillRect(0, groundY + 8, w, h - groundY - 8);

		const ppm = 80; // pixels per meter
		const centerX = w / 2;

		// Block 1 (left, blue)
		const b1Scale = Math.sqrt(mass1 / 5);
		const b1W = 40 * Math.max(0.6, Math.min(1.8, b1Scale));
		const b1H = 40 * Math.max(0.6, Math.min(1.5, b1Scale));
		const b1X = centerX + bodyState.pos1 * ppm - b1W / 2;
		const b1Y = groundY - b1H;

		// Block shadow
		ctx.fillStyle = "rgba(0,0,0,0.3)";
		ctx.fillRect(b1X + 3, b1Y + 3, b1W, b1H);

		const g1 = ctx.createLinearGradient(b1X, b1Y, b1X, b1Y + b1H);
		g1.addColorStop(0, "#3b82f6");
		g1.addColorStop(1, "#1d4ed8");
		ctx.fillStyle = g1;
		ctx.fillRect(b1X, b1Y, b1W, b1H);
		ctx.strokeStyle = "#60a5fa";
		ctx.lineWidth = 2;
		ctx.strokeRect(b1X, b1Y, b1W, b1H);

		ctx.fillStyle = "#fff";
		ctx.font = "bold 12px system-ui";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(`${mass1}kg`, b1X + b1W / 2, b1Y + b1H / 2);

		// Block 2 (right, orange)
		const b2Scale = Math.sqrt(mass2 / 5);
		const b2W = 40 * Math.max(0.6, Math.min(1.8, b2Scale));
		const b2H = 40 * Math.max(0.6, Math.min(1.5, b2Scale));
		const b2X = centerX + bodyState.pos2 * ppm - b2W / 2;
		const b2Y = groundY - b2H;

		ctx.fillStyle = "rgba(0,0,0,0.3)";
		ctx.fillRect(b2X + 3, b2Y + 3, b2W, b2H);

		const g2 = ctx.createLinearGradient(b2X, b2Y, b2X, b2Y + b2H);
		g2.addColorStop(0, "#f97316");
		g2.addColorStop(1, "#c2410c");
		ctx.fillStyle = g2;
		ctx.fillRect(b2X, b2Y, b2W, b2H);
		ctx.strokeStyle = "#fb923c";
		ctx.lineWidth = 2;
		ctx.strokeRect(b2X, b2Y, b2W, b2H);

		ctx.fillStyle = "#fff";
		ctx.fillText(`${mass2}kg`, b2X + b2W / 2, b2Y + b2H / 2);

		// Force arrows during/after collision
		if (hasCollided) {
			const arrowLen = Math.min(pushForce * 1.5, 120);

			// Force on block 2 from block 1 (action)
			drawArrowCanvas(
				ctx,
				b2X - 5,
				b2Y + b2H / 2 - 8,
				b2X - 5 + arrowLen,
				b2Y + b2H / 2 - 8,
				"#22c55e",
				`F₁₂ = ${pushForce}N`,
			);

			// Force on block 1 from block 2 (reaction)
			drawArrowCanvas(
				ctx,
				b1X + b1W + 5,
				b1Y + b1H / 2 + 8,
				b1X + b1W + 5 - arrowLen,
				b1Y + b1H / 2 + 8,
				"#ef4444",
				`F₂₁ = -${pushForce}N`,
			);

			// Equal and opposite label
			ctx.fillStyle = "#fbbf24";
			ctx.font = "bold 14px system-ui";
			ctx.textAlign = "center";
			ctx.fillText("Equal & Opposite!", w / 2, 30);
		}

		// Velocity arrows
		if (Math.abs(bodyState.vel1) > 0.1) {
			const vLen = Math.min(Math.abs(bodyState.vel1) * 15, 80);
			const dir = Math.sign(bodyState.vel1);
			drawArrowCanvas(
				ctx,
				b1X + b1W / 2,
				b1Y - 20,
				b1X + b1W / 2 + dir * vLen,
				b1Y - 20,
				"#a78bfa",
				`v = ${Math.abs(bodyState.vel1).toFixed(1)}`,
			);
		}
		if (Math.abs(bodyState.vel2) > 0.1) {
			const vLen = Math.min(Math.abs(bodyState.vel2) * 15, 80);
			const dir = Math.sign(bodyState.vel2);
			drawArrowCanvas(
				ctx,
				b2X + b2W / 2,
				b2Y - 20,
				b2X + b2W / 2 + dir * vLen,
				b2Y - 20,
				"#a78bfa",
				`v = ${Math.abs(bodyState.vel2).toFixed(1)}`,
			);
		}

		// Info panel
		ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
		ctx.fillRect(8, 8, 160, 50);
		ctx.strokeStyle = "#334155";
		ctx.lineWidth = 1;
		ctx.strokeRect(8, 8, 160, 50);

		ctx.fillStyle = "#e2e8f0";
		ctx.font = "bold 12px system-ui";
		ctx.textAlign = "left";
		ctx.textBaseline = "top";
		ctx.fillText("Action-Reaction Demo", 16, 14);
		ctx.fillStyle = "#94a3b8";
		ctx.font = "12px system-ui";
		ctx.fillText(
			`Phase: ${phase === "approach" ? "Approaching" : phase === "contact" ? "Collision!" : "Separating"}`,
			16,
			32,
		);

		ctx.restore();
	}, [bodyState, mass1, mass2, pushForce, hasCollided, phase]);

	useEffect(() => {
		draw();
	}, [draw]);

	// Static push demo state
	const [staticForce, setStaticForce] = useState(25);

	return (
		<div className="space-y-6">
			<div className="space-y-3">
				<div className="flex items-center gap-3">
					<Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-sm px-3 py-1">
						Law 3
					</Badge>
					<h2 className="text-2xl font-bold">
						Newton's Third Law: Action-Reaction
					</h2>
				</div>
				<blockquote className="border-l-4 border-orange-500 pl-4 italic text-muted-foreground text-lg">
					"For every action, there is an equal and opposite reaction."
				</blockquote>
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-4">
					<h3 className="font-semibold text-lg">
						Forces Come in Pairs
					</h3>
					<p className="text-muted-foreground leading-relaxed">
						Whenever object A exerts a force on object B, object B
						simultaneously exerts an <strong className="text-foreground">equal and opposite</strong> force on
						object A. These are called <strong className="text-foreground">action-reaction pairs</strong>.
					</p>
					<p className="text-muted-foreground leading-relaxed">
						The key insight: these paired forces act on{" "}
						<strong className="text-foreground">
							different objects
						</strong>
						. When you push a wall, the wall pushes back on you with
						the same force. When a hammer hits a nail, the nail
						pushes back on the hammer equally hard.
					</p>
					<div className="flex items-center justify-center gap-6 py-4">
						<div className="text-center">
							<div className="w-16 h-16 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-2xl font-bold text-blue-400">
								A
							</div>
							<p className="text-xs text-muted-foreground mt-1">
								Object A
							</p>
						</div>
						<div className="flex flex-col items-center gap-1">
							<div className="flex items-center gap-1 text-green-400 text-sm font-mono">
								F<sub>AB</sub> →
							</div>
							<ArrowLeftRight className="w-8 h-8 text-amber-400" />
							<div className="flex items-center gap-1 text-red-400 text-sm font-mono">
								← F<sub>BA</sub>
							</div>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-2xl font-bold text-orange-400">
								B
							</div>
							<p className="text-xs text-muted-foreground mt-1">
								Object B
							</p>
						</div>
					</div>
					<p className="text-center text-sm font-mono text-amber-400">
						F<sub>AB</sub> = -F<sub>BA</sub> &nbsp;(equal magnitude,
						opposite direction)
					</p>
				</CardContent>
			</Card>

			<Separator />

			{/* Static Force Pair Demo */}
			<div className="space-y-4">
				<h3 className="font-semibold text-lg flex items-center gap-2">
					<ArrowLeftRight className="w-5 h-5 text-orange-500" />
					Force Pair Visualizer
				</h3>
				<p className="text-sm text-muted-foreground">
					Adjust the force and see how both forces in the pair always
					remain equal and opposite.
				</p>

				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<Label className="text-sm font-medium">
							Push Force
						</Label>
						<Badge variant="secondary" className="font-mono">
							{staticForce} N
						</Badge>
					</div>
					<Slider
						value={[staticForce]}
						onValueChange={([v]) => setStaticForce(v)}
						min={5}
						max={100}
						step={1}
					/>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<Card className="bg-green-500/10 border-green-500/30">
						<CardContent className="pt-4 pb-3 text-center">
							<p className="text-xs text-muted-foreground mb-1">
								Action Force (A → B)
							</p>
							<p className="text-2xl font-mono font-bold text-green-400">
								+{staticForce} N →
							</p>
						</CardContent>
					</Card>
					<Card className="bg-red-500/10 border-red-500/30">
						<CardContent className="pt-4 pb-3 text-center">
							<p className="text-xs text-muted-foreground mb-1">
								Reaction Force (B → A)
							</p>
							<p className="text-2xl font-mono font-bold text-red-400">
								← -{staticForce} N
							</p>
						</CardContent>
					</Card>
				</div>
			</div>

			<Separator />

			{/* Collision Demo */}
			<div className="space-y-4">
				<h3 className="font-semibold text-lg flex items-center gap-2">
					<ArrowLeftRight className="w-5 h-5 text-orange-500" />
					Collision Experiment
				</h3>
				<p className="text-sm text-muted-foreground">
					Watch what happens when two objects collide. Despite the
					forces being equal, objects with different masses accelerate
					differently (Law 2: a = F/m).
				</p>

				<canvas
					ref={canvasRef}
					style={{ width: 700, height: 280 }}
					className="rounded-lg border border-border w-full max-w-full"
				/>

				<div className="flex flex-wrap gap-3 items-center">
					<Button
						onClick={() => setIsRunning(!isRunning)}
						className="flex items-center gap-2"
						variant={isRunning ? "secondary" : "default"}
					>
						{isRunning ? (
							<>
								<Pause className="w-4 h-4" /> Pause
							</>
						) : (
							<>
								<Play className="w-4 h-4" /> Play
							</>
						)}
					</Button>
					<Button
						onClick={reset}
						variant="outline"
						className="flex items-center gap-2"
					>
						<RotateCcw className="w-4 h-4" /> Reset
					</Button>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label className="text-sm font-medium text-blue-400">
								Block A Mass
							</Label>
							<Badge variant="secondary" className="font-mono">
								{mass1} kg
							</Badge>
						</div>
						<Slider
							value={[mass1]}
							onValueChange={([v]) => {
								setMass1(v);
								reset();
							}}
							min={1}
							max={20}
							step={1}
						/>
					</div>
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label className="text-sm font-medium text-orange-400">
								Block B Mass
							</Label>
							<Badge variant="secondary" className="font-mono">
								{mass2} kg
							</Badge>
						</div>
						<Slider
							value={[mass2]}
							onValueChange={([v]) => {
								setMass2(v);
								reset();
							}}
							min={1}
							max={20}
							step={1}
						/>
					</div>
				</div>

				{hasCollided && (
					<Card className="bg-orange-500/10 border-orange-500/30">
						<CardContent className="pt-4 pb-3 space-y-2">
							<h4 className="font-semibold text-orange-400">
								Collision Analysis
							</h4>
							<p className="text-sm text-muted-foreground">
								Both blocks experienced the same force magnitude
								during collision, but:
							</p>
							<div className="grid grid-cols-2 gap-3 text-sm font-mono">
								<div>
									<span className="text-blue-400">
										Block A ({mass1}kg):
									</span>
									<br />a = F/{mass1} ={" "}
									{(pushForce / mass1).toFixed(1)} m/s²
								</div>
								<div>
									<span className="text-orange-400">
										Block B ({mass2}kg):
									</span>
									<br />a = F/{mass2} ={" "}
									{(pushForce / mass2).toFixed(1)} m/s²
								</div>
							</div>
							<p className="text-xs text-muted-foreground mt-2">
								{mass1 < mass2
									? "The lighter block (A) bounces back faster because the same force produces more acceleration on a smaller mass!"
									: mass1 > mass2
										? "The heavier block (A) barely changes direction while the lighter block (B) flies away!"
										: "Equal masses: they exchange velocities perfectly!"}
							</p>
						</CardContent>
					</Card>
				)}
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-3">
					<h3 className="font-semibold">Key Takeaways</h3>
					<ul className="space-y-2 text-sm text-muted-foreground">
						<li className="flex items-start gap-2">
							<span className="text-orange-400 mt-0.5">1.</span>
							Forces always come in <strong className="text-foreground">pairs</strong> -- you cannot have a
							single isolated force.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-orange-400 mt-0.5">2.</span>
							Action and reaction forces are{" "}
							<strong className="text-foreground">
								equal in magnitude
							</strong>{" "}
							and{" "}
							<strong className="text-foreground">
								opposite in direction
							</strong>
							.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-orange-400 mt-0.5">3.</span>
							The paired forces act on{" "}
							<strong className="text-foreground">
								different objects
							</strong>{" "}
							-- they don't cancel each other out.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-orange-400 mt-0.5">4.</span>
							Even though the forces are equal, different masses
							experience different accelerations (connecting Law 3
							back to Law 2).
						</li>
					</ul>
				</CardContent>
			</Card>
		</div>
	);
}

function drawArrowCanvas(
	ctx: CanvasRenderingContext2D,
	fromX: number,
	fromY: number,
	toX: number,
	toY: number,
	color: string,
	label?: string,
) {
	const headLen = 10;
	const angle = Math.atan2(toY - fromY, toX - fromX);

	ctx.strokeStyle = color;
	ctx.fillStyle = color;
	ctx.lineWidth = 3;
	ctx.lineCap = "round";

	ctx.beginPath();
	ctx.moveTo(fromX, fromY);
	ctx.lineTo(toX, toY);
	ctx.stroke();

	ctx.beginPath();
	ctx.moveTo(toX, toY);
	ctx.lineTo(
		toX - headLen * Math.cos(angle - Math.PI / 6),
		toY - headLen * Math.sin(angle - Math.PI / 6),
	);
	ctx.lineTo(
		toX - headLen * Math.cos(angle + Math.PI / 6),
		toY - headLen * Math.sin(angle + Math.PI / 6),
	);
	ctx.closePath();
	ctx.fill();

	if (label) {
		ctx.font = "bold 11px system-ui";
		ctx.textAlign = "center";
		ctx.textBaseline = "bottom";
		ctx.fillText(label, (fromX + toX) / 2, Math.min(fromY, toY) - 5);
	}
}
